import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import type { InlineKind } from "./InlineCell";

export interface BulkField {
  field: string;
  label: string;
  kind: InlineKind | "owner";
  options?: { value: string; label: string }[];
  /** The value may be cleared (an empty box sets it empty) */
  nullable?: boolean;
}

const inputCls = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-primary";

/**
 * "Edit a field" for the selected rows: choose the field, give one value, apply it to all of them. Rows that can't take it
 * (converted, locked, out of reach) are skipped by the server and counted, the rest are changed.
 */
export function BulkEditDialog({ open, onClose, list, ids, fields, onDone }: { open: boolean; onClose: () => void; list: string; ids: string[]; fields: BulkField[]; onDone: () => void }) {
  const { success, error } = useToast();
  const [field, setField] = useState(fields[0]?.field || "");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [people, setPeople] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const def = fields.find((f) => f.field === field) || fields[0];

  useEffect(() => {
    if (!open) return;
    setField(fields[0]?.field || "");
    setValue("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && def?.kind === "owner" && !people.length) api.get("/api/User/Assignable").then((r) => setPeople(unwrapArray(r))).catch(() => undefined);
  }, [open, def?.kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!def) return;
    setSaving(true);
    try {
      const payload = def.kind === "datetime" && value ? new Date(value).toISOString() : value;
      const r = unwrapObject<{ updated: number; skipped: number; reasons: string[] }>(await api.post(`/api/InlineEdit/${list}/Bulk`, { ids, field: def.field, value: payload }));
      success(`${def.label} changed`, `${r.updated} changed${r.skipped ? `; ${r.skipped} skipped${r.reasons?.length ? ` (${r.reasons.join("; ")})` : ""}` : ""}`);
      onClose();
      onDone();
    } catch (err: any) {
      error("Couldn't change the field", err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const type = def?.kind === "number" ? "number" : def?.kind === "date" ? "date" : def?.kind === "datetime" ? "datetime-local" : def?.kind === "email" ? "email" : "text";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit a field</DialogTitle>
          <DialogDescription>
            Set one field on the {ids.length} selected {ids.length === 1 ? "record" : "records"}. Locked or out-of-reach ones are skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block space-y-1 text-xs font-medium text-muted-foreground">
            Field
            <select
              value={field}
              onChange={(e) => {
                setField(e.target.value);
                setValue("");
              }}
              className={inputCls}
            >
              {fields.map((f) => (
                <option key={f.field} value={f.field}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-xs font-medium text-muted-foreground">
            New value{def?.nullable ? " (leave empty to clear)" : ""}
            {def?.kind === "enum" || def?.kind === "owner" ? (
              <select value={value} onChange={(e) => setValue(e.target.value)} className={inputCls}>
                <option value="">Choose…</option>
                {def.kind === "owner"
                  ? people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {`${p.first_name} ${p.last_name || ""}`.trim()}
                      </option>
                    ))
                  : (def.options || []).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
              </select>
            ) : (
              <input type={type} step={def?.kind === "number" ? "any" : undefined} min={def?.kind === "number" ? 0 : undefined} value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} />
            )}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !ids.length || (!def?.nullable && !value)} onClick={submit}>
            {saving ? "Applying…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
